// src/utils/visitorTrackingClientUtils.js

export const VISITOR_TRACKING_STORAGE_KEYS = {
  visitorToken:
    "monappliclub_visitor_token",

  sessionToken:
    "monappliclub_visitor_session_token",

  attribution:
    "monappliclub_visitor_attribution",
};

/**
 * Paramètres publicitaires utiles pour déterminer
 * la provenance, mais inutiles dans le nom de la page
 * affiché dans l'administration.
 */
const TRACKING_QUERY_PARAMETERS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",

  "fbclid",
  "gclid",
  "dclid",
  "msclkid",
  "ttclid",

  "mc_cid",
  "mc_eid",
];

/**
 * Lecture sécurisée du localStorage.
 */
function readLocalStorage(key) {
  try {
    return window.localStorage.getItem(key);
  } catch (error) {
    console.warn(
      "Lecture du localStorage impossible :",
      error,
    );

    return null;
  }
}

/**
 * Écriture sécurisée dans le localStorage.
 */
function writeLocalStorage(key, value) {
  try {
    if (
      value === undefined ||
      value === null ||
      value === ""
    ) {
      window.localStorage.removeItem(key);
      return;
    }

    window.localStorage.setItem(
      key,
      value,
    );
  } catch (error) {
    console.warn(
      "Écriture dans le localStorage impossible :",
      error,
    );
  }
}

/**
 * Lecture sécurisée du sessionStorage.
 */
function readSessionStorage(key) {
  try {
    return window.sessionStorage.getItem(key);
  } catch (error) {
    console.warn(
      "Lecture du sessionStorage impossible :",
      error,
    );

    return null;
  }
}

/**
 * Écriture sécurisée dans le sessionStorage.
 */
function writeSessionStorage(key, value) {
  try {
    if (
      value === undefined ||
      value === null ||
      value === ""
    ) {
      window.sessionStorage.removeItem(key);
      return;
    }

    window.sessionStorage.setItem(
      key,
      value,
    );
  } catch (error) {
    console.warn(
      "Écriture dans le sessionStorage impossible :",
      error,
    );
  }
}

/**
 * Lecture sécurisée d'un objet JSON.
 */
function readSessionJson(key) {
  const value =
    readSessionStorage(key);

  if (!value) {
    return null;
  }

  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
}

/**
 * Retourne l'identifiant anonyme persistant
 * du navigateur.
 */
export function getStoredVisitorToken() {
  return readLocalStorage(
    VISITOR_TRACKING_STORAGE_KEYS.visitorToken,
  );
}

/**
 * Enregistre l'identifiant anonyme persistant
 * du navigateur.
 */
export function storeVisitorToken(token) {
  writeLocalStorage(
    VISITOR_TRACKING_STORAGE_KEYS.visitorToken,
    token,
  );
}

/**
 * Retourne le token de la session courante.
 */
export function getStoredSessionToken() {
  return readSessionStorage(
    VISITOR_TRACKING_STORAGE_KEYS.sessionToken,
  );
}

/**
 * Enregistre le token de la session courante.
 */
export function storeSessionToken(token) {
  writeSessionStorage(
    VISITOR_TRACKING_STORAGE_KEYS.sessionToken,
    token,
  );
}

/**
 * Supprime uniquement la session courante.
 *
 * Le visitor_token reste conservé pour permettre
 * d'identifier un visiteur revenant.
 */
export function clearStoredSessionToken() {
  writeSessionStorage(
    VISITOR_TRACKING_STORAGE_KEYS.sessionToken,
    null,
  );
}

/**
 * Supprime toutes les données anonymes du tracker.
 */
export function clearVisitorTrackingStorage() {
  writeLocalStorage(
    VISITOR_TRACKING_STORAGE_KEYS.visitorToken,
    null,
  );

  writeSessionStorage(
    VISITOR_TRACKING_STORAGE_KEYS.sessionToken,
    null,
  );

  writeSessionStorage(
    VISITOR_TRACKING_STORAGE_KEYS.attribution,
    null,
  );
}

/**
 * Retourne une URL interne propre.
 *
 * Les paramètres de campagne sont supprimés de la page
 * enregistrée afin d'éviter des lignes telles que :
 *
 * /?fbclid=xxxxxxxxxxxxxxxx
 *
 * dans les tableaux et les futurs graphiques.
 */
export function buildTrackedPage(location) {
  const pathname =
    location?.pathname ||
    window.location.pathname ||
    "/";

  const search =
    location?.search ??
    window.location.search ??
    "";

  const params =
    new URLSearchParams(search);

  TRACKING_QUERY_PARAMETERS.forEach(
    (parameter) => {
      params.delete(parameter);
    },
  );

  /**
   * Trie les paramètres restants afin que deux URL
   * identiques avec un ordre différent soient regroupées.
   */
  params.sort();

  const cleanSearch =
    params.toString();

  return cleanSearch
    ? `${pathname}?${cleanSearch}`
    : pathname;
}

/**
 * Extrait les informations de campagne présentes
 * dans l'URL.
 */
function extractAttributionFromLocation(
  location,
) {
  const search =
    location?.search ??
    window.location.search ??
    "";

  const params =
    new URLSearchParams(search);

  return {
    utm_source:
      params.get("utm_source") ||
      "",

    utm_medium:
      params.get("utm_medium") ||
      "",

    utm_campaign:
      params.get("utm_campaign") ||
      "",

    utm_term:
      params.get("utm_term") ||
      "",

    utm_content:
      params.get("utm_content") ||
      "",

    referrer:
      document.referrer || "",
  };
}

/**
 * Indique si une attribution contient une information
 * réellement exploitable.
 */
function hasAttributionValue(
  attribution,
) {
  if (!attribution) {
    return false;
  }

  return Boolean(
    attribution.utm_source ||
      attribution.utm_medium ||
      attribution.utm_campaign ||
      attribution.utm_term ||
      attribution.utm_content ||
      attribution.referrer,
  );
}

/**
 * Retourne l'attribution d'origine de la session.
 *
 * Elle est mémorisée dans le sessionStorage afin que
 * les changements de routes React ne la remplacent pas.
 */
export function getOrCreateAttribution(
  location,
) {
  const storedAttribution =
    readSessionJson(
      VISITOR_TRACKING_STORAGE_KEYS.attribution,
    );

  if (
    hasAttributionValue(
      storedAttribution,
    )
  ) {
    return storedAttribution;
  }

  const currentAttribution =
    extractAttributionFromLocation(
      location,
    );

  writeSessionStorage(
    VISITOR_TRACKING_STORAGE_KEYS.attribution,
    JSON.stringify(
      currentAttribution,
    ),
  );

  return currentAttribution;
}

/**
 * Détecte le navigateur et sa version sans dépendance.
 */
function detectBrowser(userAgent) {
  const ua = userAgent || "";

  const rules = [
    {
      name: "Edge",
      regex: /Edg\/([\d.]+)/i,
    },
    {
      name: "Opera",
      regex: /OPR\/([\d.]+)/i,
    },
    {
      name: "Chrome",
      regex: /Chrome\/([\d.]+)/i,
    },
    {
      name: "Firefox",
      regex: /Firefox\/([\d.]+)/i,
    },
    {
      name: "Safari",
      regex:
        /Version\/([\d.]+).*Safari/i,
    },
  ];

  for (const rule of rules) {
    const match =
      ua.match(rule.regex);

    if (match) {
      return {
        browser_name:
          rule.name,

        browser_version:
          match[1] || "",
      };
    }
  }

  return {
    browser_name: "Inconnu",
    browser_version: "",
  };
}

/**
 * Détecte le système d'exploitation.
 */
function detectOperatingSystem(
  userAgent,
) {
  const ua = userAgent || "";

  if (/Windows NT/i.test(ua)) {
    return "Windows";
  }

  if (/Android/i.test(ua)) {
    return "Android";
  }

  if (
    /iPhone|iPad|iPod/i.test(
      ua,
    )
  ) {
    return "iOS";
  }

  if (/Mac OS X/i.test(ua)) {
    return "macOS";
  }

  if (/Linux/i.test(ua)) {
    return "Linux";
  }

  return "Inconnu";
}

/**
 * Détecte le type d'appareil.
 */
function detectDeviceType(
  userAgent,
) {
  const ua = userAgent || "";

  if (
    /iPad|Tablet|PlayBook|Silk/i.test(
      ua,
    )
  ) {
    return "tablet";
  }

  if (
    /Mobi|Android|iPhone|iPod/i.test(
      ua,
    )
  ) {
    return "mobile";
  }

  return "desktop";
}

/**
 * Retourne les informations techniques envoyées
 * à la création d'une session.
 */
export function getVisitorDeviceData() {
  const userAgent =
    navigator.userAgent || "";

  const browser =
    detectBrowser(userAgent);

  return {
    device_type:
      detectDeviceType(
        userAgent,
      ),

    browser_name:
      browser.browser_name,

    browser_version:
      browser.browser_version,

    os_name:
      detectOperatingSystem(
        userAgent,
      ),

    language:
      navigator.language || "",

    timezone:
      Intl.DateTimeFormat()
        .resolvedOptions()
        .timeZone || "",

    screen_width:
      window.screen?.width ||
      window.innerWidth ||
      null,

    screen_height:
      window.screen?.height ||
      window.innerHeight ||
      null,
  };
}

/**
 * Retourne le titre courant avec une longueur raisonnable.
 */
export function getTrackedPageTitle() {
  return (
    document.title
      ?.toString()
      .trim()
      .slice(0, 255) ||
    "Page sans titre"
  );
}

/**
 * Détermine si une route doit être exclue.
 */
export function isExcludedTrackingPath(
  pathname,
  excludedPathPrefixes = [],
) {
  const normalizedPath =
    (pathname || "/").toLowerCase();

  return excludedPathPrefixes.some(
    (prefix) => {
      const normalizedPrefix =
        prefix
          .toString()
          .toLowerCase();

      return (
        normalizedPath ===
          normalizedPrefix ||
        normalizedPath.startsWith(
          `${normalizedPrefix}/`,
        )
      );
    },
  );
}

/**
 * Les erreurs 404 et 409 du tracking indiquent
 * généralement qu'une nouvelle session doit être créée.
 */
export function isRecoverableSessionError(
  error,
) {
  const status =
    Number(
      error?.response?.status,
    ) || 0;

  const code =
    error?.response?.data?.code;

  return (
    status === 404 ||
    status === 409 ||
    code ===
      "SESSION_NOT_FOUND" ||
    code === "SESSION_EXPIRED"
  );
}